import { useEffect } from 'react';
import axios from 'axios';

/**
 * Pobiera zawartość pliku TXT
 */
const fetchFile = async (directory: 'header' | 'scripts', fileName: string): Promise<string | null> => {
  try {
    const url = `/generated/analitycs/${directory}/${fileName}.txt?t=${Date.now()}`;
    const response = await axios.get(url, {
      responseType: 'text',
      headers: { 'Cache-Control': 'no-cache' }
    });
    return response.data;
  } catch {
    return null;
  }
};

/**
 * Pobiera listę dostępnych plików
 */
const fetchFileList = async (directory: 'header' | 'scripts'): Promise<string[]> => {
  try {
    const url = `/generated/analitycs/${directory}/index.json?t=${Date.now()}`;
    const response = await axios.get(url, {
      headers: { 'Cache-Control': 'no-cache' }
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
};

/**
 * Wkleja HTML do DOM-u
 */
const insertHTML = (html: string, target: HTMLElement): void => {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  Array.from(temp.childNodes).forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      element.setAttribute('data-analytics', 'true');

      // Skrypty muszą być przekopiowane, żeby się wykonały
      if (element.tagName === 'SCRIPT') {
        const script = document.createElement('script');
        Array.from(element.attributes).forEach(attr => {
          script.setAttribute(attr.name, attr.value);
        });
        script.textContent = element.textContent;
        script.setAttribute('data-analytics', 'true');
        target.appendChild(script);
      } else {
        target.appendChild(element);
      }
    }
  });
};

/**
 * Usuwa poprzednie skrypty analytics
 */
const cleanup = (): void => {
  document.querySelectorAll('[data-analytics="true"]').forEach(el => el.remove());
};

// Globalne zmienne, które przetrwają remount w StrictMode
let globalLoadTimeout: NodeJS.Timeout | null = null;
let isLoadScheduled = false;

/**
 * Hook do ładowania analytics
 */
export const useAnalyticsTracking = () => {
  useEffect(() => {
    // Sprawdź czy już jest zaplanowane ładowanie (przetrwa remount w StrictMode)
    if (isLoadScheduled && globalLoadTimeout) {
      return;
    }

    const load = async () => {
      try {
        cleanup();

        // Wczytaj header files
        const headerFiles = await fetchFileList('header');
        for (const file of headerFiles) {
          const html = await fetchFile('header', file);
          if (html) {
            insertHTML(html, document.head);
          }
        }

        // Wczytaj scripts files
        const scriptFiles = await fetchFileList('scripts');
        for (const file of scriptFiles) {
          const html = await fetchFile('scripts', file);
          if (html) {
            insertHTML(html, document.body);
          }
        }

        isLoadScheduled = false;
        globalLoadTimeout = null;
      } catch {
        isLoadScheduled = false;
        globalLoadTimeout = null;
      }
    };

    // Opóźnij ładowanie analytics o 5 sekund
    const DELAY_MS = 5000;

    // Wyczyść poprzedni timeout jeśli istnieje
    if (globalLoadTimeout) {
      clearTimeout(globalLoadTimeout);
    }

    globalLoadTimeout = setTimeout(() => {
      globalLoadTimeout = null;

      if (document.readyState === 'complete') {
        load();
      } else {
        window.addEventListener('load', load, { once: true });
      }
    }, DELAY_MS);

    isLoadScheduled = true;

    return () => {
      // Nie czyść globalnego timeoutu - musi się wykonać mimo remount
    };
  }, []);
};
